import { useCallback, useEffect, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AdminPermissions } from 'constants/admin-permissions';
import { CC_STATUS_DISPLAY } from 'constants/statuses';
import { CC_STATUS_ICON_DISPLAY } from 'constants/statuses';
import { getCheckTypeIcon } from 'helpers/iconsHelper';
import { useAccessControl } from 'hooks/access-control';
import { useDocumentTitle } from 'hooks/document-title';
import { useSupportUserNames } from 'hooks/use-support-user-names';
import AdminPage from 'layouts/AdminPage/AdminPage';
import moment from 'moment';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { Button } from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';
import StatusPill from 'components/Pills/StatusPills';
import { Table, useTableContext } from 'components/Table/Table';
import { CustomToast } from 'components/Toast/CustomToast';

import { selectAuthentication } from 'api/authentication-slice';
import {
  useLazyDownloadAdminCandidateChecksReportQuery,
  useLazyGetAllCandidateChecksQuery,
} from 'api/candidate-checks-api';
import { useGetCheckTypesQuery } from 'api/check-types-api';
import { useGetAllCompaniesListQuery } from 'api/company-api';

import { CandidateCheckNotesModal } from './AdminCandidateCheckNotesModal';

const ApplyFiltersButton = () => {
  const { manuallyApplyFilters } = useTableContext();

  return (
    <Button icon="filter" onClick={manuallyApplyFilters}>
      Apply Filters
    </Button>
  );
};

const AdminCandidateChecks = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);

  const authentication = useSelector(selectAuthentication);
  const hasPermission = useAccessControl(authentication.admin_permissions);

  const [candidateChecks, setCandidateChecks] = useState([]);
  const [companiesOptions, setCompaniesOptions] = useState([]);

  const [showNotes, setShowNotes] = useState(false);
  const [selectedCheckId, setSelectedCheckId] = useState(null);

  const [getAllCandidateChecks] = useLazyGetAllCandidateChecksQuery();
  const [downloadAdminCandidateChecksReport] = useLazyDownloadAdminCandidateChecksReportQuery();
  const { data: companiesData } = useGetAllCompaniesListQuery();
  const { data: checkTypes } = useGetCheckTypesQuery();

  const [checkType, setCheckType] = useState();

  useDocumentTitle('Candidate Checks');

  const [pagination, setPagination] = useState({
    page: 1,
    records_per_page: 15,
    total_pages: 1,
    total_records: 0,
  });

  const OVERDUE_SUPPORT_HOURS = 15;
  const todayPlusBuffer = moment().add(2, 'days').format('YYYY-MM-DD');
  const sixMonthsAgo = moment().subtract(6, 'months').format('YYYY-MM-DD');

  const defaultQuery = {
    page: 1,
    status: {
      value: ['submitted', 'in-progress'],
      name: [t('common.submitted'), t('common.inProgress')],
      type: 'status',
    },
    check_type: {
      value: ['support_checks'],
      name: ['Support Checks'],
      type: 'status',
    },
    in_progress_sub_status: {
      value: [
        'processing',
        'waiting_on_client',
        'waiting_on_candidate',
        'waiting_on_provider',
        'deferred',
      ],
      name: [
        'Processing',
        'Waiting on Client',
        'Waiting on Candidate',
        'Waiting on Provider',
        'Deferred',
      ],
      type: 'status',
    },
    submitted_at: {
      value: [sixMonthsAgo, todayPlusBuffer],
    },
  };

  const [tableQuery, setTableQuery] = useState(defaultQuery);

  const { supportUsers } = useSupportUserNames();

  useEffect(() => {
    if (!companiesData) return;
    const list = Array.isArray(companiesData.result) ? companiesData.result : [];
    setCompaniesOptions([
      { name: t('common.all', { element: 'companies' }), value: 'all' },
      ...list.map(c => ({ name: c?.name ?? '', value: c?.id ?? '' })),
    ]);
  }, [companiesData]);

  const fetchCandidateChecks = async params => {
    setLoading(true);
    try {
      const { result } = await getAllCandidateChecks({
        ...params,
        overdue_support_hours: OVERDUE_SUPPORT_HOURS,
      }).unwrap();
      setPagination(result?.pagination);
      setCandidateChecks(Array.isArray(result?.candidate_checks) ? result.candidate_checks : []);
      if (params?.check_type && params?.check_type !== 'all')
        setCheckType((checkTypes || [])?.find(checkType => checkType.slug === params?.check_type));
      setLoading(false);
    } catch (error) {
      toast.custom(
        t => (
          <CustomToast
            t={t}
            title="Error fetching checks"
            message={String(
              error?.data?.message || error?.data?.error || error?.message || 'Unknown error',
            )}
            type="error"
          />
        ),
        {
          duration: 5000,
        },
      );
    } finally {
      setLoading(false);
    }
  };

  const formatMySqlUtcToLocalDate = ts => {
    if (!ts) return null;
    return new Date(ts.replace(' ', 'T') + 'Z');
  };

  const mapCCStatus = useCallback(status => {
    return CC_STATUS_DISPLAY[status]?.color || 'neutral';
  }, []);

  const download = async () => {
    setLoading(true);
    await downloadAdminCandidateChecksReport({
      queryParams: tableQuery,
      overdue_support_hours: OVERDUE_SUPPORT_HOURS,
    }).unwrap();
    setLoading(false);
  };

  return (
    <AdminPage loading={loading} title="Checks" allowedRole={AdminPermissions.ListCandidateChecks}>
      {checkTypes?.length > 0 && (
        <Table.Context
          defaultQuery={defaultQuery}
          manualQueryUpdate={true}
          onQueryUpdate={query => {
            setTableQuery(query);
            fetchCandidateChecks(query);
          }}>
          <Table.FloatingFilters className="u-margin--large surface-white">
            <Table.FloatingFiltersLeft className="u-gap-medium align-items-end">
              <Table.DateRangeFilter
                title="Created Date"
                filterName="created_at"
                labelFrom="From"
                labelTo="To"
              />
              <Table.DateRangeFilter
                title="Submitted Date"
                filterName="submitted_at"
                labelFrom="From"
                labelTo="To"
              />
              <Table.DateRangeFilter
                title="Completed Date"
                filterName="completed_at"
                labelFrom="From"
                labelTo="To"
              />
              <Table.MultiSelectFilter
                title="Result File Status"
                filterName="upload_id_status"
                type="status"
                options={[
                  { name: 'No Upload', value: 'no_upload' },
                  { name: 'Has Upload', value: 'has_upload' },
                ]}
                useDefault={false}
              />
              <Table.Filter
                title="Company"
                filterName="company"
                options={companiesOptions}
                useDefault={false}
                useSearch={true}
              />
              <Table.MultiSelectFilter
                title="Type"
                filterName="check_type"
                type="status"
                options={[
                  { name: t('common.all', { element: 'types' }), value: 'all' },
                  { name: 'Support Checks', value: 'support_checks' },
                  ...checkTypes?.map(checkType => ({
                    name: checkType.title,
                    value: checkType.slug,
                  })),
                ]}
                useSearchInMultiSelect={true}
              />
              <Table.MultiSelectFilter
                title="Status"
                filterName="status"
                type="status"
                options={[
                  { name: t('common.inProgress'), value: 'in-progress' },
                  { name: t('common.submitted'), value: 'submitted' },
                  { name: t('common.completed'), value: 'completed' },
                  { name: t('common.candidate'), value: 'candidate' },
                  { name: t('common.canceled'), value: 'canceled' },
                ]}
              />
              <Table.MultiSelectFilter
                title="In-progress status"
                filterName="in_progress_sub_status"
                type="status"
                useDefault={false}
                options={[
                  { name: 'Processing', value: 'processing' },
                  { name: 'Waiting on Client', value: 'waiting_on_client' },
                  { name: 'Waiting on Candidate', value: 'waiting_on_candidate' },
                  { name: 'Waiting on Provider', value: 'waiting_on_provider' },
                  { name: 'Blocked', value: 'blocked' },
                  { name: 'Deferred', value: 'deferred' },
                ]}
              />
              <Table.MultiSelectFilter
                title="Bot Status"
                filterName="bot_status"
                type="status"
                useDefault={false}
                options={[
                  { name: 'Bot Failed', value: 'failed' },
                  { name: 'Successfully Submitted', value: 'submitted' },
                ]}
              />
              <Table.MultiSelectFilter
                title="Overdue Status"
                filterName="overdue_status"
                type="status"
                options={[
                  {
                    name: `Overdue Support (${OVERDUE_SUPPORT_HOURS}h)`,
                    value: 'overdue_support',
                  },
                  { name: 'Overdue Provider', value: 'overdue_provider' },
                ]}
                useDefault={false}
              />
              <Table.Filter
                title="Support User"
                filterName="user"
                options={[
                  { name: t('common.all', { element: 'support users' }), value: 'all' },
                  ...(supportUsers || []).map(user => ({
                    name: `${user.first_name} ${user.last_name}`,
                    value: user.id,
                  })),
                ]}
                useDefault={false}
              />
            </Table.FloatingFiltersLeft>
            <Table.FloatingFiltersRight>
              <ApplyFiltersButton onApply={fetchCandidateChecks} />
            </Table.FloatingFiltersRight>
          </Table.FloatingFilters>
          <Table.TableContent className="u-margin--large">
            <Table.Filters>
              <Table.LeftFilters>
                <Table.Search placeholder="Search for a candidate..." />
              </Table.LeftFilters>
              <Table.RightFilters>
                <Button icon="download" onClick={download}>
                  Export
                </Button>
              </Table.RightFilters>
            </Table.Filters>
            <Table className="u-margin--large">
              <Table.Head>
                <Table.Row key="header-row">
                  <Table.Header key="id">{t('common.id')}</Table.Header>
                  <Table.Header key="name" sortKey="candidates.first_name">
                    {t('common.name')}
                  </Table.Header>
                  <Table.Header key="type">{t('common.checks')}</Table.Header>
                  <Table.Header key="status">{t('common.status')}</Table.Header>
                  <Table.Header key="created" sortKey="candidate_checks.created_at">
                    {t('common.createdAt')}
                  </Table.Header>
                  <Table.Header key="submitted" sortKey="candidate_checks.submitted_at">
                    {t('common.submittedAt')}
                  </Table.Header>
                  <Table.Header key="completed" sortKey="candidate_checks.completed_at">
                    {t('common.completedAt')}
                  </Table.Header>
                  <Table.Header key="company">Company Name</Table.Header>
                  <Table.Header key="internal_history">Notes</Table.Header>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {(candidateChecks || []).map((candidateCheck, index) => {
                  const currentCheckType =
                    checkType ||
                    (checkTypes || []).find(ct => ct.slug === candidateCheck?.check_type);

                  return (
                    <Table.Row
                      key={`check-${candidateCheck.candidate_check_id ?? candidateCheck.candidate_id}-${index}`}>
                      <Table.Col>
                        {hasPermission(AdminPermissions.ProcessCandidateChecks) ? (
                          <Link
                            to={`/admin/candidates/${candidateCheck.candidate_id}`}
                            className="u-link dark">
                            {candidateCheck.candidate_check_id ?? candidateCheck.candidate_id}
                          </Link>
                        ) : (
                          <span>
                            {candidateCheck.candidate_check_id ?? candidateCheck.candidate_id}
                          </span>
                        )}
                      </Table.Col>
                      <Table.Col>
                        <div className="d-flex flex-column u-gap-small align-items-start">
                          {`${candidateCheck.first_name} ${candidateCheck.last_name}`}
                          <span className="u-font-size-x-small secondary-text">
                            {candidateCheck.candidate_email}
                          </span>
                        </div>
                      </Table.Col>
                      <Table.Col>
                        <IconComponent
                          size="small"
                          tip={currentCheckType.title}
                          noBackground
                          type={CC_STATUS_ICON_DISPLAY[candidateCheck.status].color}
                          icon={getCheckTypeIcon(currentCheckType?.slug)}
                        />
                      </Table.Col>
                      <Table.Col>
                        <div className="d-flex flex-wrap gap-1 align-items-center">
                          <StatusPill
                            text={
                              candidateCheck.status === 'in-progress'
                                ? candidateCheck.in_progress_sub_status
                                : candidateCheck.status
                            }
                            status={mapCCStatus(candidateCheck.status)}
                          />

                          {candidateCheck?.bot_status === 'failed' && (
                            <StatusPill text="bot failed" status="error" />
                          )}
                          {candidateCheck?.bot_status === 'submitted' && (
                            <StatusPill text="successfully submitted" status="success" />
                          )}
                          {!!candidateCheck?.overdue_support && (
                            <StatusPill text={`overdue support`} status={'error'} />
                          )}
                          {!!candidateCheck?.overdue_provider && (
                            <StatusPill text={`overdue provider`} status={'error'} />
                          )}
                        </div>
                      </Table.Col>
                      <Table.Col>
                        {candidateCheck.created_at && (
                          <Moment format="DD/MM/YYYY">
                            {formatMySqlUtcToLocalDate(candidateCheck?.created_at)}
                          </Moment>
                        )}
                      </Table.Col>
                      <Table.Col>
                        {candidateCheck.submitted_at && (
                          <Moment format="DD/MM/YYYY">
                            {formatMySqlUtcToLocalDate(candidateCheck?.submitted_at)}
                          </Moment>
                        )}
                      </Table.Col>
                      <Table.Col>
                        <div className="d-flex u-gap-small align-items-center">
                          {candidateCheck.completed_at && (
                            <Moment format="DD/MM/YYYY">
                              {formatMySqlUtcToLocalDate(candidateCheck?.completed_at)}
                            </Moment>
                          )}

                          {candidateCheck.upload_id_status === 'no_upload' && (
                            <OverlayTrigger overlay={<Tooltip>No upload</Tooltip>}>
                              <FontAwesomeIcon
                                icon="file-circle-xmark"
                                className="u-txt-status-overdue"
                              />
                            </OverlayTrigger>
                          )}
                        </div>
                      </Table.Col>
                      <Table.Col>{candidateCheck.company_name}</Table.Col>
                      <Table.Col>
                        <div
                          className="u-link dark"
                          onClick={() => {
                            setSelectedCheckId(candidateCheck?.candidate_check_id);
                            setShowNotes(true);
                          }}>
                          Click for notes
                        </div>
                      </Table.Col>
                    </Table.Row>
                  );
                })}
              </Table.Body>
              <Table.Pagination {...pagination} />
            </Table>
          </Table.TableContent>
        </Table.Context>
      )}
      {selectedCheckId && (
        <CandidateCheckNotesModal
          checkId={selectedCheckId}
          visible={showNotes}
          onClose={() => setShowNotes(false)}
        />
      )}
    </AdminPage>
  );
};

export default AdminCandidateChecks;
